import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useMutation, useQuery } from 'convex/react'
import { useEffect, useRef } from 'react'

import { projectTodayTasks } from '@/hooks/use-offline-mutations'
import { todayKey } from '@/lib/day'
import { readSnapshot, writeSnapshot } from '@/lib/offline-cache'
import { useOfflineStore } from '@/store/offline.store'

const TODAY_CACHE_KEY = 'today-ctx'

export type TodayTask = {
  taskId: Id<'nodes'>
  title: string
  icon?: string | null
  description: string | null
  tags: string[]
  goalTitle: string
  goalId: Id<'nodes'> | null
  milestoneTitle: string
  milestoneId: Id<'nodes'> | null
  targetTitle: string
  targetId: Id<'nodes'> | null
  category: string
  completedAt: number | null
  effort: 'low' | 'medium' | 'high'
  superUrgent?: boolean
  status: 'backlog' | 'in-progress' | 'done'
  isInbox: boolean
  dueDate: number | null
  recurs: boolean
  recursPeriod: string | null
  recursDays?: number[] | null
  recurrenceDay: number | null
  recurrenceTime: string | null
  durationMinutes: number | null
  allDay: boolean
  location?: string
  dueBefore?: boolean
  lastEditedBy?: string
  mustDo: boolean
  putOffDays: number
}

export type QueuedTask = { taskId: Id<'nodes'>; title: string }

export type BusyBlock = { start: number; end: number }

export type CalendarDayLoad = 'light' | 'moderate' | 'heavy' | 'blocked'

export type TodayState =
  | { status: 'loading' }
  | { status: 'no-goals' }
  | { status: 'no-surface' }
  | {
      status: 'all-completed'
      queued: QueuedTask[]
      resurface: () => void
      activeNudge: string | null
      calendarDayLoad: CalendarDayLoad | null
      calendarBusy: BusyBlock[]
    }
  | {
      status: 'tasks'
      tasks: TodayTask[]
      queued: QueuedTask[]
      resurface: () => void
      activeNudge: string | null
      calendarDayLoad: CalendarDayLoad | null
      calendarBusy: BusyBlock[]
    }

// Module-level — survives route unmount so navigating back doesn't flash the skeleton
let lastKnownState: Exclude<TodayState, { status: 'loading' }> | null = null

// The logical day (honors day-start hour), so the surface rolls over with habits.
export function localDateStr(): string {
  return todayKey()
}

export function useTodayTasks(): TodayState {
  const localDate = localDateStr()
  const ctxLive = useQuery(api.dailySurface.getTodayWithContext, { localDate })
  const ensureSurface = useMutation(api.dailySurface.ensureTodaySurface)
  const resurfaceMutation = useMutation(api.dailySurface.resurface)
  const queue = useOfflineStore((s) => s.queue)

  // Persist the last good payload; fall back to it when the network is down.
  const cachedRef = useRef<typeof ctxLive>(undefined)
  if (cachedRef.current === undefined)
    cachedRef.current = readSnapshot(TODAY_CACHE_KEY) ?? undefined
  useEffect(() => {
    if (ctxLive !== undefined && ctxLive !== null) {
      writeSnapshot(TODAY_CACHE_KEY, ctxLive)
      cachedRef.current = ctxLive
    }
  }, [ctxLive])

  const ctx = ctxLive ?? cachedRef.current

  useEffect(() => {
    if (ctx?.hasGoals && !ctx.surfaceExists) {
      ensureSurface({ localDate })
    }
  }, [ctx?.hasGoals, ctx?.surfaceExists]) // eslint-disable-line react-hooks/exhaustive-deps

  if (ctx === undefined || ctx === null) {
    return lastKnownState ?? { status: 'loading' }
  }

  let next: Exclude<TodayState, { status: 'loading' }>
  if (!ctx.hasGoals) {
    next = { status: 'no-goals' }
  } else if (!ctx.surfaceExists) {
    next = { status: 'no-surface' }
  } else if (ctx.allCompleted) {
    next = {
      status: 'all-completed',
      queued: ctx.queued ?? [],
      resurface: () => resurfaceMutation({ localDate }),
      activeNudge: ctx.activeNudge,
      calendarDayLoad: ctx.calendarDayLoad,
      // Older cached snapshots predate busy blocks.
      calendarBusy: ctx.calendarBusy ?? [],
    }
  } else {
    next = {
      status: 'tasks',
      tasks: projectTodayTasks(ctx.tasks, queue),
      queued: ctx.queued ?? [],
      resurface: () => resurfaceMutation({ localDate }),
      activeNudge: ctx.activeNudge,
      calendarDayLoad: ctx.calendarDayLoad,
      // Older cached snapshots predate busy blocks.
      calendarBusy: ctx.calendarBusy ?? [],
    }
  }

  lastKnownState = next
  return next
}
